import { Grid } from '@mui/material';
import './UnavailabilityBox.scss';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { AccountData, OrderPageData, UnavailabilityData, UnavailabilityDetailData } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import UnavailabilityBoxPopper from './UnavailabilityBoxPopper';
import { MAX_PARALLEL_PERSON_CAPACITY } from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/PrivateUnavailabilityDrawer';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';
import { getOrderPages } from '../../common/AccountUtils';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import {
  SchedulerGridDisplayMode,
  SchedulerResourceGrid,
} from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/SchedulerResourceGrid';

const UnavailabilityBox = (props: { unavailability: UnavailabilityData; viewType?: 'month' | 'day'; css?: any }) => {
  const { unavailability, viewType, css } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { calendarView } = useCalendar();

  const { t } = useTranslation();
  const language = useLanguage();

  const { authState } = useAuth();

  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);

  const orderPagesWithArchived = getOrderPages(account, true);
  const openUnavailability = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const getTimeText = () => {
    if (unavailability.all_day) {
      return 'All day';
    }
    return `${moment.unix(unavailability.start_time).tz('utc').format(timeFormat)} - ${moment
      .unix(unavailability.end_time)
      .tz('utc')
      .format(timeFormat)}`;
  };

  let dynamicStyles = null;
  if (css) {
    dynamicStyles = {
      width: `calc(${css.width}% - 2px)`,
      left: `${css.left}%`,
      top: `${css.top - 1}px`,
      height: `${css.height - 5}px`,
      position: 'absolute',
    };
  }

  return (
    <>
      <Grid
        onClick={openUnavailability}
        className={`unavailability-box flex-1${viewType === 'month' ? ' month-unavailability-box' : ''}`}
        sx={dynamicStyles}>
        <Grid className="text-center unavailability-box-inner">
          {viewType === 'month' && (
            <p dir="ltr">
              <strong>{getTimeText()}</strong>
            </p>
          )}
          <p>
            <span>{unavailability.description ? unavailability.description : t('calendar.unavailability')}</span>
            {unavailability.order_page_id && (
              <span>
                - {orderPagesWithArchived.filter((o: any) => o.id === unavailability.order_page_id)[0].label[language]}
              </span>
            )}
          </p>
          {unavailability.custom_details && (
            <>
              {Object.keys(unavailability.custom_details).map((key) => {
                return (
                  <p dir="ltr">
                    <span>{unavailability.custom_details?.[key]}</span>
                    &nbsp;
                    <span>{key === MAX_PARALLEL_PERSON_CAPACITY ? t('availabilitySettings.blockResources') : key}</span>
                  </p>
                );
              })}
            </>
          )}
          {!isEmpty(unavailability.unavailability_details) &&
            unavailability.unavailability_details!.length < orderPagesWithArchived.length && (
              <Grid container justifyContent="center" gap="2px" my="4px">
                {unavailability.unavailability_details?.map((x: UnavailabilityDetailData) => {
                  const customDetails = orderPagesWithArchived.find(
                    (o: OrderPageData) => o.id === x.order_page_id,
                  )?.custom_details;
                  const keyName = customDetails?.[0]?.name;
                  return (
                    <WorkshopCircle
                      withTooltip
                      account={account}
                      orderPageId={
                        orderPagesWithArchived.find((orderPage: OrderPageData) => orderPage.id === x.order_page_id)?.id
                      }
                      language={language}
                      customDetailAmoount={
                        x.custom_details && keyName && x.custom_details?.[keyName] > 1
                          ? x.custom_details?.[keyName]
                          : undefined
                      }
                    />
                  );
                })}
              </Grid>
            )}
          {(unavailability.shared_resources_blocks?.automatic_dividable_resources?.length || 0) > 0 && (
            <Grid container justifyContent="center" gap={1} my="4px">
              {unavailability.shared_resources_blocks?.automatic_dividable_resources?.map((block) => {
                const resource = account.shared_resources?.automatic_dividable_resources?.find(
                  (r) => r.id === block.resource_id,
                );
                if (!resource) return null;

                return (
                  <p dir="ltr" key={block.resource_id}>
                    <span>{resource.label[language]}: </span>
                    <span>{block.blocked_capacity}</span>
                  </p>
                );
              })}
            </Grid>
          )}
          {(unavailability.shared_resources_blocks?.scheduler_configs?.length || 0) > 0 &&
            calendarView !== CalendarView.DAY && (
              <Grid container justifyContent="center" gap={1} my="4px" flexDirection="column">
                {unavailability.shared_resources_blocks?.scheduler_configs?.map((block) => {
                  const config = account.shared_resources?.scheduler_configs?.find(
                    (c) => c.id === block.scheduler_config_id,
                  );
                  if (!config) {
                    return null;
                  }

                  const blockedPositions = block.blocked_mask
                    .split('')
                    .map((bit, index) => (bit === '1' ? index : -1))
                    .filter((pos) => pos !== -1);
                  if (blockedPositions.length === 0) {
                    return null;
                  }
                  return (
                    <SchedulerResourceGrid
                      key={block.scheduler_config_id}
                      schedulerConfig={config}
                      blockedMask={block.blocked_mask}
                      readOnly
                      displayMode={SchedulerGridDisplayMode.INLINE}
                    />
                  );
                })}
              </Grid>
            )}
        </Grid>
      </Grid>
      <UnavailabilityBoxPopper anchorEl={anchorEl} setAnchorEl={setAnchorEl} unavailability={unavailability} />
    </>
  );
};

export default UnavailabilityBox;
